import { useState, useEffect } from 'react';
import { THEMES, getTheme, applyTheme, loadSavedTheme, saveTheme, ThemeConfig } from '@/lib/themes';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Palette, Check } from 'lucide-react';
import { toast } from '@/hooks/use-toast';

export function ThemeSwitcher() {
  const [currentTheme, setCurrentTheme] = useState<string>(loadSavedTheme());
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const theme = getTheme(currentTheme);
    applyTheme(theme);
  }, [currentTheme]);

  const handleThemeChange = (themeId: string) => {
    const theme = getTheme(themeId);
    setCurrentTheme(theme.id);
    saveTheme(theme.id);
    toast({
      title: 'Theme applied',
      description: `Switched to ${theme.name}`,
    });
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Palette className="w-5 h-5" />
          <span className="sr-only">Change theme</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Palette className="w-5 h-5" />
            Choose Skin
          </DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          {Object.values(THEMES).map((theme) => (
            <ThemeCard
              key={theme.id}
              theme={theme}
              isActive={currentTheme === theme.id}
              onSelect={() => handleThemeChange(theme.id)}
            />
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface ThemeCardProps {
  theme: ThemeConfig;
  isActive: boolean;
  onSelect: () => void;
}

function ThemeCard({ theme, isActive, onSelect }: ThemeCardProps) {
  // Preview colors from the theme
  const previewColors = [
    theme.colors.primary,
    theme.colors.accent,
    theme.colors.secondary,
    ...theme.colors.months.slice(0, 4),
  ];

  return (
    <button
      onClick={onSelect}
      className={`
        relative w-full p-4 rounded-xl border-2 text-left transition-all
        ${isActive 
          ? 'border-primary bg-primary/10' 
          : 'border-border hover:border-primary/50 hover:bg-secondary/30'
        }
      `}
    >
      {/* Active indicator */}
      {isActive && (
        <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-primary flex items-center justify-center">
          <Check className="w-4 h-4 text-primary-foreground" />
        </div>
      )}
      
      {/* Theme preview */}
      <div className="flex gap-3 mb-3">
        {/* Background preview */}
        <div 
          className="w-16 h-16 rounded-lg border border-border/50 overflow-hidden"
          style={{ backgroundColor: `hsl(${theme.colors.background})` }}
        >
          {/* Mini UI preview */}
          <div className="p-1.5 h-full flex flex-col">
            <div 
              className="h-2 w-8 rounded-sm mb-1"
              style={{ backgroundColor: `hsl(${theme.colors.primary})` }}
            />
            <div 
              className="flex-1 rounded-sm"
              style={{ backgroundColor: `hsl(${theme.colors.card})` }}
            >
              <div className="p-0.5 flex gap-0.5 flex-wrap">
                {previewColors.slice(0, 4).map((color, i) => (
                  <div
                    key={i}
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: `hsl(${color})` }}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Color swatches */}
        <div className="flex-1">
          <h3 className="font-display font-semibold text-sm mb-1">{theme.name}</h3>
          <p className="text-xs text-muted-foreground mb-2">{theme.description}</p>
          <div className="flex gap-1">
            {previewColors.slice(0, 7).map((color, i) => (
              <div
                key={i}
                className="w-4 h-4 rounded-full border border-border/30"
                style={{ backgroundColor: `hsl(${color})` }}
              />
            ))}
          </div>
        </div>
      </div>
    </button>
  );
}
